import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import FormattedListValue, {
  listItemMsg,
  formatListValue,
} from '@/shared/components/FormattedListValue';
import moment from 'moment';
import Checkbox from '@/shared/components/input/Checkbox';
import OkCancelModal from '@/shared/components/OkCancelModal';
import DatePicker from '@/shared/components/input/DatePicker';
import messages from './trainingMessages';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import { falseFunc, validateMinimumDate, validateEndDate } from '@/shared/utils/validationHelpers';
import classNames from 'classnames';
import ResourceAutoComplete from '@/shared/components/registration/ResourceAutoComplete';

class TrainingModal extends Component {
  state = { resources: [], saving: false };

  static propTypes = {
    show: PropTypes.bool,
    isEdit: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    resources: PropTypes.array,
  };

  static defaultProps = {
    show: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      training: props.training
        ? {
            ...props.training,
            startDate: moment(props.training.startDate),
            endDate: moment(props.training.endDate),
          }
        : {
            name: '',
            resource: '',
            durationHours: 0,
            type: null,
            status: null,
            startDate: moment(),
            endDate: moment().add(1, 'day'),
            mandatory: false,
            certificateRequired: false,
            description: '',
          },
      touchedFields: {},
      showValidation: false,
    };
  }

  componentDidMount() {
    this.retrieveResources();
  }

  handleChange = (field, value) => {
    this.setState(prevState => ({
      training: {
        ...prevState.training,
        [field]: value,
      },
      touched: {
        ...prevState.touched,
        [field]: true,
      },
    }));
  };

  retrieveResources = () => {
    const { intl } = this.props;
    var type = 5;
    api.base
      .get(`${config.appUrls.api}/api/Resource/Registration?type=${type}`)
      .then(({ data }) => {
        this.setState({ resources: data });

        // Optionally auto-select if there's only one
        const onlyResource = data.length === 1 ? data[0] : null;
        if (onlyResource && !this.state.training.resource) {
          this.handleChange('resource', onlyResource);
          this.handleChange('resourceId', onlyResource.id);
        }
      })
      .catch(error => {
        console.error('Failed to load resources:', error);
      });
  };

  validateTraining = () => {
    const { training } = this.state;

    return (
      training.name != null &&
      training.resourceId != null &&
      training.type != null &&
      training.status != null &&
      training.startDate != null &&
      training.endDate != null &&
      moment(training.endDate)
        .startOf('day')
        .isSameOrAfter(moment(training.startDate).startOf('day'))
    );
  };

  saveTraining = async () => {
    if (this.state.saving) return;

    this.setState({ saving: true });
    const { training } = this.state;
    const { onSave, onClose, intl } = this.props;

    if (!this.validateTraining()) {
      this.setState({ showValidation: true, saving: false });
      return;
    }

    try {
      let response;
      if (!training.id) {
        response = await api.base.post(`${config.appUrls.api}/api/Training/post`, training);
        ToastrService.success(intl.formatMessage(messages.TrainingCreated));
      } else {
        response = await api.base.put(`${config.appUrls.api}/api/Training/put`, training);
        ToastrService.success(intl.formatMessage(messages.TrainingUpdated));
      }

      onSave(response.data);
      onClose();
    } catch (err) {
      handleHttpError(intl)(err);
      console.error('Failed to save training', err);
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const { show, onClose, intl, isEdit } = this.props;
    const { training, startDateFocused, endDateFocused } = this.state;
    const title = intl.formatMessage(isEdit ? messages.EditTraining : messages.CreateTraining);

    return (
      <OkCancelModal
        title={title}
        show={show}
        onClose={onClose}
        onSave={this.saveTraining}
        disabled={this.state.saving}
      >
        <form className="form-horizontal form-labels-left container-fluid">
          <div
            className={classNames('form-group', {
              'has-error': this.state.showValidation && !training.name?.trim(),
            })}
          >
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.Name} />
            </label>
            <div className="col-sm-8">
              <input
                type="text"
                maxLength="150"
                autoFocus
                value={training.name}
                placeholder={intl.formatMessage(messages.Name)}
                onChange={e => this.handleChange('name', e.target.value)}
                required
                className="form-control"
              />
            </div>
          </div>

          <div
            className={classNames('form-group', {
              'has-error': this.state.showValidation && !this.state.training.resource,
            })}
          >
            <label className="control-label col-sm-4">{intl.formatMessage(messages.What)}</label>
            <div className="col-sm-8">
              {this.state.resources?.length > 0 && (
                <ResourceAutoComplete
                  items={this.state.resources}
                  value={this.state.training.resource}
                  placeholder={intl.formatMessage(messages.SearchPriceListItem)}
                  onChange={resource => {
                    this.handleChange('resource', resource);
                    this.handleChange('resourceId', resource?.id ?? null);
                  }}
                  emptyLabel={intl.formatMessage(messages.NoPriceListItemsFound)}
                  required
                  disabled={false}
                  isSmall
                  intl={intl}
                />
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.ExpectedTrainingHours} />
            </label>
            <div className="col-sm-8">
              <input
                type="number"
                min="0"
                className="form-control"
                value={training.durationHours}
                onChange={e => this.handleChange('durationHours', parseFloat(e.target.value))}
              />
            </div>
          </div>

          <div
            className={classNames('form-group', {
              'has-error':
                this.state.showValidation &&
                (training.type === null || training.type === undefined),
            })}
          >
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.Type} />
            </label>
            <div className="col-sm-8">
              <select
                className="form-control"
                value={training.type ?? ''}
                required
                onChange={e =>
                  this.handleChange('type', e.target.value === '' ? null : parseInt(e.target.value))
                }
              >
                <option value="">{}</option>
                <option value="0">{intl.formatMessage(listItemMsg('trainingType', '0'))}</option>
                <option value="1">{intl.formatMessage(listItemMsg('trainingType', '1'))}</option>
              </select>
            </div>
          </div>

          <div
            className={classNames('form-group', {
              'has-error':
                this.state.showValidation &&
                (training.status === null || training.status === undefined),
            })}
          >
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.Status} />
            </label>
            <div className="col-sm-8">
              <select
                className="form-control"
                value={training.status ?? ''}
                required
                onChange={e =>
                  this.handleChange(
                    'status',
                    e.target.value === '' ? null : parseInt(e.target.value),
                  )
                }
              >
                <option value="">{}</option>
                <option value="0">{intl.formatMessage(listItemMsg('trainingStatus', '0'))}</option>
                <option value="1">{intl.formatMessage(listItemMsg('trainingStatus', '1'))}</option>
                <option value="2">{intl.formatMessage(listItemMsg('trainingStatus', '2'))}</option>
                <option value="3">{intl.formatMessage(listItemMsg('trainingStatus', '3'))}</option>
              </select>
            </div>
          </div>

          <div
            className={classNames('form-group', {
              'has-error':
                this.state.showValidation &&
                (!training.startDate || !validateMinimumDate(training.startDate)),
            })}
          >
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.StartDate} />
            </label>
            <div className="col-sm-8">
              <DatePicker
                date={training.startDate}
                onDateChange={date => this.handleChange('startDate', date)}
                focused={startDateFocused}
                onFocusChange={({ focused }) => this.setState({ startDateFocused: focused })}
                isOutsideRange={falseFunc}
              />
            </div>
          </div>

          <div
            className={classNames('form-group', {
              'has-error':
                this.state.showValidation &&
                (!training.endDate ||
                  !validateMinimumDate(training.endDate) ||
                  (training.startDate &&
                    validateMinimumDate(training.startDate) &&
                    !validateEndDate(training.endDate, training.startDate))),
            })}
          >
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.EndDate} />
            </label>
            <div className="col-sm-8">
              <DatePicker
                date={training.endDate}
                onDateChange={date => this.handleChange('endDate', date)}
                focused={endDateFocused}
                onFocusChange={({ focused }) => this.setState({ endDateFocused: focused })}
                isOutsideRange={falseFunc}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.Mandatory} />
            </label>
            <div className="col-sm-8">
              <Checkbox
                value={training.mandatory}
                onClick={() => this.handleChange('mandatory', !training.mandatory)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.CertificateRequired} />
            </label>
            <div className="col-sm-8">
              <Checkbox
                value={training.certificateRequired}
                onClick={() =>
                  this.handleChange('certificateRequired', !training.certificateRequired)
                }
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.Description} />
            </label>
            <div className="col-sm-8">
              <textarea
                className="form-control"
                value={training.description}
                onChange={e => this.handleChange('description', e.target.value)}
              />
            </div>
          </div>
        </form>
      </OkCancelModal>
    );
  }
}

export default injectIntl(TrainingModal);
